/** Movimentações do tribunal (DataJud e MNI) de um processo, com a conferência contra as intimações e o teor sob demanda. */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "@/lib/router";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import { Badge, Card, ErrorBox, Loading, Modal, ReasonDialog, useAct } from "./kit";
import { dedupMovements } from "./process-timeline";

export interface Movement {
  id: string; processId: string; code: number | null; name: string; complement: string | null; occurredAt: string; degree: string | null; organ: string | null;
  relevant: boolean; matchedCommunicationId: string | null; dismissedAt: string | null; historical?: boolean;
  source?: string | null; teorAvailable?: boolean; hasTeor?: boolean;
}

/** "Ver teor": documento vinculado à movimentação, baixado do tribunal a pedido (pode configurar ciência). */
export function TeorButton({ m, invalidate }: { m: Movement; invalidate: string[][] }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!m.teorAvailable && !m.hasTeor) return null;
  return (
    <>
      <button className={`btn small ${m.hasTeor ? "" : "ghost"}`} onClick={() => setOpen(true)}>{m.hasTeor ? "Ver teor" : "Ver teor…"}</button>
      {open && <TeorModal m={m} invalidate={invalidate} onClose={() => setOpen(false)} />}
    </>
  );
}

function TeorModal({ m, invalidate, onClose }: { m: Movement; invalidate: string[][]; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const key = ["teor", m.id];
  const { data, error } = useQuery({ queryKey: key, queryFn: () => api.get<{ available: boolean; fetched: boolean; text: string | null; documentId: string | null; fetchedAt: string | null; fetchedBy: string | null; pendingNotices: number }>(`/processos/${m.processId}/movimentacoes/${m.id}/teor`) });
  const [assumo, setAssumo] = useState(false);
  const fetchIt = useAct(() => api.post(`/processos/${m.processId}/movimentacoes/${m.id}/teor`, { assumoCiencia: assumo }), [...invalidate, key, ["documentos"]], undefined, "Teor baixado do tribunal");
  const need = (data?.pendingNotices ?? 0) > 0;
  return (
    <Modal title={`Teor — ${m.name}`} onClose={onClose} wide="xl" footer={<button className="btn" onClick={() => { void qc.invalidateQueries({ queryKey: key }); onClose(); }}>Fechar</button>}>
      <div className="stack gap-12 small">
        <div className="muted">{fmtDate(m.occurredAt)}{m.degree ? ` · ${m.degree}` : ""}{m.complement ? ` · ${m.complement}` : ""}</div>
        <ErrorBox error={error ?? fetchIt.error} />
        {!data ? <Loading /> : data.fetched ? (
          <>
            <div className="muted">Baixado do tribunal por {data.fetchedBy ?? "—"} em {data.fetchedAt ? fmtDateTime(data.fetchedAt) : "—"}. {data.documentId && <a href="#" onClick={(e) => { e.preventDefault(); void api.download(`/documentos/${data.documentId}/download?inline=1`, "documento", true); }}>Abrir o arquivo original</a>}</div>
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13, lineHeight: 1.5, maxHeight: "60vh", overflow: "auto", background: "var(--surface-2, #f8fafc)", padding: 12, borderRadius: 8 }}>{data.text}</pre>
          </>
        ) : (
          <>
            <div>O documento desta movimentação ainda não foi baixado. Ao baixar, o VG APP busca no tribunal (MNI), guarda o original em <b>Documentos</b> do processo e o texto aqui — depois disso ninguém precisa abrir o PJe para ler.</div>
            {need
              ? <div className="warn-box">
                  <b>Atenção: há {data.pendingNotices} intimação(ões) não analisada(s) neste processo.</b> Ler o teor de uma decisão com intimação pendente pode configurar <b>ciência</b> e iniciar o prazo (Lei 11.419, art. 5º).
                  <label className="row nowrap" style={{ marginTop: 8 }}><input type="checkbox" checked={assumo} onChange={(e) => setAssumo(e.target.checked)} /> Estou ciente e assumo a ciência — quero ver o teor.</label>
                </div>
              : <div className="muted">Não há intimação pendente registrada neste processo. A visualização fica registrada (quem e quando).</div>}
            <div className="row"><button className="btn primary" disabled={fetchIt.isPending || (need && !assumo)} onClick={() => fetchIt.mutate()}>{fetchIt.isPending ? "Buscando no tribunal…" : "Baixar e ver o teor"}</button></div>
          </>
        )}
      </div>
    </Modal>
  );
}

export function MovementStatus({ m }: { m: Movement }): JSX.Element | null {
  if (!m.relevant) return null;
  if (m.matchedCommunicationId) return <Link to={`/controladoria/intimacoes/${m.matchedCommunicationId}?fila=todas`}><Badge tone="ok">intimação capturada</Badge></Link>;
  if (m.dismissedAt) return <Badge tone="muted">conferida</Badge>;
  return <Badge tone="warn">sem intimação capturada</Badge>;
}

export function DismissButton({ id, invalidate }: { id: string; invalidate: string[][] }): JSX.Element | null {
  const { can } = useLookups();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  if (!can("comunicacao.triar")) return null;
  return (
    <>
      <button className="btn small ghost" onClick={() => setOpen(true)}>Conferida</button>
      {open && (
        <ReasonDialog title="Marcar movimentação como conferida" label="Por que não há intimação a tratar?" confirm="Marcar"
          onClose={() => setOpen(false)}
          onSubmit={async (reason) => {
            await api.post(`/movimentacoes/${id}/conferida`, { reason });
            await Promise.all([...invalidate, ["controladoria"]].map((k) => qc.invalidateQueries({ queryKey: k })));
          }} />
      )}
    </>
  );
}

export function ProcessMovements({ processId, hasCnj }: { processId: string; hasCnj: boolean }): JSX.Element {
  const key = ["movimentacoes", processId];
  const { data, error } = useQuery({ queryKey: key, queryFn: () => api.get<{ checkedAt: string | null; updatedAt: string | null; error: string | null; movements: Movement[] }>(`/processos/${processId}/movimentacoes`) });
  const [onlyRelevant, setOnlyRelevant] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [src, setSrc] = useState<"unica" | "tribunal" | "datajud" | "todas">("unica");
  const check = useAct(() => api.post<{ found: boolean; created: number }>(`/processos/${processId}/datajud`), [key, ["processo"]], undefined, "Consulta ao DataJud concluída");
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const hist = data.movements.filter((m) => m.historical).length;
  const bySrc = src === "todas" ? data.movements : src === "tribunal" ? data.movements.filter((m) => m.source === "tribunal") : src === "datajud" ? data.movements.filter((m) => m.source !== "tribunal")
    : dedupMovements(data.movements.map((m) => ({ ...m, kind: "mov", at: m.occurredAt, source: m.source ?? null, extra: { code: m.code } })));
  const rows = (onlyRelevant ? bySrc.filter((m) => m.relevant) : bySrc).filter((m) => showHist || !m.historical);
  return (
    <Card title="Movimentações do tribunal" actions={
      <div className="row nowrap">
        <select className="input" style={{ width: "auto" }} value={src} onChange={(e) => setSrc(e.target.value as typeof src)}>
          <option value="unica">Sem repetir</option><option value="tribunal">Só tribunal (MNI)</option><option value="datajud">Só DataJud</option><option value="todas">Todas as fontes</option>
        </select>
        <label className="row nowrap small"><input type="checkbox" checked={onlyRelevant} onChange={(e) => setOnlyRelevant(e.target.checked)} /> só as que podem gerar prazo</label>
        {hasCnj && <button className="btn small" disabled={check.isPending} onClick={() => check.mutate()}>{check.isPending ? "Consultando…" : "Consultar agora"}</button>}
      </div>
    }>
      <div className="muted small" style={{ marginBottom: 8 }}>
        {data.checkedAt ? <>Última consulta: {fmtDateTime(data.checkedAt)}{data.updatedAt ? ` · dados do tribunal atualizados em ${fmtDateTime(data.updatedAt)}` : ""}</> : "Ainda não consultado — a conferência automática roda algumas vezes ao dia."}
        {data.error && <div className="text-bad">{data.error}</div>}
        <div>Vêm do conector de tribunais (MNI, quase em tempo real) e do DataJud (atraso de horas a dias). "Ver teor" baixa do tribunal o documento da movimentação, quando houver.</div>
      </div>
      <ErrorBox error={check.error} />
      <div className="timeline">
        {rows.map((m) => (
          <div key={m.id} className={`timeline-item ${m.historical ? "historical" : ""}`}>
            <div className="when">{fmtDateTime(m.occurredAt)} · {m.source === "tribunal" ? "tribunal (MNI)" : "DataJud"}{m.degree ? ` · ${m.degree}` : ""}{m.organ ? ` · ${m.organ}` : ""}</div>
            <div className="row" style={{ gap: 6 }}>
              <span style={{ fontWeight: m.relevant ? 600 : 400 }}>{m.name}</span>
              <MovementStatus m={m} />
              {m.relevant && !m.matchedCommunicationId && !m.dismissedAt && <DismissButton id={m.id} invalidate={[key]} />}
              <TeorButton m={m} invalidate={[key]} />
            </div>
            {m.complement && <div className="muted small">{m.complement}</div>}
          </div>
        ))}
        {!rows.length && <div className="muted small">Nenhuma movimentação.</div>}
        {hist > 0 && <button className="btn small ghost" onClick={() => setShowHist(!showHist)}>{showHist ? "Ocultar" : "Mostrar"} histórico anterior ao acompanhamento ({hist})</button>}
      </div>
    </Card>
  );
}
